import Draft from '@/calendesk/models/DTO/Response/Draft'
import api from '@/lib/calendesk-js-library/api/api'
import Section from '@/calendesk/models/DTO/Response/Section'
import Page from '@/calendesk/models/DTO/Response/Page'
import { ChangeStep, ChangeStepType } from '@/calendesk/models/ChangeStep'
import store from '@/store/index'
import { RestoredStep, RestoreType } from '@/calendesk/models/RestoredStep'
import SectionImage from '../models/DTO/Response/SectionImage'
import { SectionsSectionType, SectionType } from '@/calendesk/models/BuilderTypes'
import { BuilderState } from '@/store/modules/builder/types'
import DraftWithPage from '@/calendesk/models/DraftWithPage'
import { plainToClass } from 'class-transformer'
import cloneClassObject from '@/calendesk/tools/cloneClassObject'
import { SectionImagePayload } from '@/calendesk/models/SectionImagePayload'
import WebsiteStyleModel from '@/calendesk/models/WebsiteStyleModel'
import { BuilderSelectType } from '@/calendesk/models/BuilderSelectType'
import { SelectionOrderType } from '@/calendesk/models/SelectionOrderType'
import { EmployeeSelectionModeType } from '@/calendesk/models/EmployeeSelectionModeType'
import { SaveStatus } from '@/calendesk/models/SaveStatus'
import { trans } from '@/lib/calendesk-js-library/prototypes/trans'
import { addMissingCookieBannerKeys } from '@/calendesk/models/CookieBannerSettings'
import { currentBookingSummary } from '@/calendesk/tools/stockBookingSummary'

declare global {
  interface Window { DraftController: any }
}

// Bumped whenever new configuration keys are introduced: the back-fill in
// checkIfPlainDraftIsMissingAnyConfiguration only runs for drafts whose
// stored version differs, so without a bump existing websites never get
// the new keys and their settings stay invisible in the panel.
export const DRAFT_VERSION = 74

const SAVE_DEBOUNCE_MS = 1000

interface PendingSave {
  timeout: number;
  run: () => Promise<any>;
  // A save that already failed once: it waits for a manual retry or the next
  // flush instead of a timer, and keeps the status on ERROR.
  failed?: boolean;
}

export default class DraftStateController {
  private static instance: DraftStateController;

  // One pending save per target (the draft, or a single section): edits to
  // different targets never cancel each other's save, and flushPendingSaves
  // can push everything out at once (publish, undo, tab close).
  private pendingSaves = new Map<string, PendingSave>()
  private inFlightSaves = new Set<Promise<any>>()

  public static getInstance (): DraftStateController {
    if (!DraftStateController.instance) {
      DraftStateController.instance = new DraftStateController()
      window.DraftController = DraftStateController.instance
    }

    return DraftStateController.instance
  }

  public updateDraft (draft: Draft): Promise<void> {
    store.commit('builder/SET_WEBSITE_STYLE_MODEL', WebsiteStyleModel.getModelForDraft(draft))

    return api.updateWb2Draft(draft.id, {
      configuration: draft.configuration
    })
  }

  public scheduleDraftSave (draft: Draft): void {
    this.scheduleSave('draft', () => this.updateDraft(draft))
  }

  public scheduleSectionSave (draft: Draft, section: Section): void {
    this.scheduleSave(`section:${section.id}`, () => this.updateSection(draft, section))
  }

  public flushPendingSaves (): Promise<any> {
    const started = Array.from(this.pendingSaves.keys()).map((key) => this.firePendingSave(key))

    return Promise.all([...started, ...Array.from(this.inFlightSaves)])
  }

  public hasPendingSaves (): boolean {
    return this.pendingSaves.size > 0 || this.inFlightSaves.size > 0
  }

  private scheduleSave (key: string, run: () => Promise<any>): void {
    const pending = this.pendingSaves.get(key)

    if (pending) {
      clearTimeout(pending.timeout)
    }

    // Replacing the run is safe: it always carries the latest committed state
    // for this target, which already includes every earlier buffered change.
    const timeout = window.setTimeout(() => {
      this.firePendingSave(key).catch(() => undefined)
    }, SAVE_DEBOUNCE_MS)
    this.pendingSaves.set(key, { timeout, run })

    store.commit('builder/SET_SAVE_STATUS', SaveStatus.SAVING)
  }

  private firePendingSave (key: string): Promise<any> {
    const pending = this.pendingSaves.get(key)

    if (!pending) {
      return Promise.resolve()
    }

    clearTimeout(pending.timeout)
    this.pendingSaves.delete(key)

    const save = pending.run().then((result: any) => {
      this.inFlightSaves.delete(save)
      this.syncSaveStatus()
      return result
    }).catch((error: any) => {
      this.inFlightSaves.delete(save)
      // Keep the failed save around (without a timer) so a retry, publish or
      // undo flush can send it again instead of losing the change.
      if (!this.pendingSaves.has(key)) {
        this.pendingSaves.set(key, { timeout: 0, run: pending.run, failed: true })
      }
      this.syncSaveStatus()
      throw error
    })
    this.inFlightSaves.add(save)

    return save
  }

  private syncSaveStatus (): void {
    const pending = Array.from(this.pendingSaves.values())

    if (this.inFlightSaves.size > 0 || pending.some((save) => !save.failed)) {
      return
    }

    if (pending.some((save) => save.failed)) {
      store.commit('builder/SET_SAVE_STATUS', SaveStatus.ERROR)
      return
    }

    store.commit('builder/SET_SAVE_STATUS', SaveStatus.SAVED)
    store.commit('builder/SET_LAST_SAVED_AT', Date.now())
  }

  public updateSectionsIfNeeded (draft: Draft) {
    draft.pages.forEach((page: Page) => {
      page.sections?.forEach((section: Section) => {
        let updateSection = false

        if (section.configuration.component_id === SectionsSectionType.CALENDAR_V2) {
          if (typeof section.configuration.wb_expand_collapse_service_panels__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_expand_collapse_service_panels__checkbox__ = false
          }

          if (typeof section.configuration.wb_selection_order__select__ === 'undefined') {
            updateSection = true
            section.configuration.wb_selection_order__select__ = {
              type: BuilderSelectType.SELECTION_ORDER,
              // Sections created before this option always started with the location choice.
              value: SelectionOrderType.LOCATION_SERVICE_EMPLOYEE
            }
          }

          if (typeof section.configuration.wb_service_search__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_service_search__checkbox__ = false
          }

          if (typeof section.configuration.wb_location_search__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_location_search__checkbox__ = false
          }

          if (typeof section.configuration.wb_employee_search__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_employee_search__checkbox__ = false
          }

          if (typeof section.configuration.wb_separate_online_locations__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_separate_online_locations__checkbox__ = false
          }

          if (typeof section.configuration.wb_container_fluid__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_container_fluid__checkbox__ = false
          }

          if (typeof section.configuration.wb_employee_selection_mode__select__ === 'undefined') {
            updateSection = true
            // The select replaced wb_any_employee_enabled__checkbox__. The old
            // key is dropped here so the sidebar never shows both controls;
            // published drafts that never reopen still carry it and the client
            // keeps reading it as a fallback.
            section.configuration.wb_employee_selection_mode__select__ = {
              type: BuilderSelectType.EMPLOYEE_SELECTION_MODE,
              value: section.configuration.wb_any_employee_enabled__checkbox__
                ? EmployeeSelectionModeType.AUTO_ASSIGN
                : EmployeeSelectionModeType.ANY_PERSON_FIRST
            }
            delete section.configuration.wb_any_employee_enabled__checkbox__
          }
        }

        if (section.configuration.component_id === SectionsSectionType.CALENDAR_V3) {
          // Both arrived after CalendarV3 shipped; they match the CalendarV2 defaults.
          if (typeof section.configuration.wb_show_booked_slots__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_show_booked_slots__checkbox__ = true
          }

          if (typeof section.configuration.wb_use_booking_cart__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_use_booking_cart__checkbox__ = true
          }

          if (typeof section.configuration.wb_container_fluid__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_container_fluid__checkbox__ = false
          }

          if (typeof section.configuration.wb_view_flat__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_view_flat__checkbox__ = false
          }

          if (typeof section.configuration.wb_employee_selection_mode__select__ === 'undefined') {
            updateSection = true
            section.configuration.wb_employee_selection_mode__select__ = {
              type: BuilderSelectType.EMPLOYEE_SELECTION_MODE,
              value: section.configuration.wb_any_employee_enabled__checkbox__
                ? EmployeeSelectionModeType.AUTO_ASSIGN
                : EmployeeSelectionModeType.ANY_PERSON_FIRST
            }
            delete section.configuration.wb_any_employee_enabled__checkbox__
          }
        }

        if (section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_1 ||
          section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_2 ||
          section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_3 ||
          section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_4 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_1 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_2 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_3 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_4 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_5 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_6) {
          if (typeof section.configuration.wb_initial_location_select__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_initial_location_select__checkbox__ = true
          }
        }

        if (section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_2 ||
          section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_3 ||
          section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_4 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_1 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_2 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_3 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_4 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_5 ||
          section.configuration.component_id === SectionsSectionType.BOOKING_6) {
          if (typeof section.configuration.wb_show_booked_slots__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_show_booked_slots__checkbox__ = false
          }
        }

        if (section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_1 ||
          section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_2 ||
          section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_3 ||
          section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_4) {
          if (typeof section.configuration.wb_init_calendar_title__html_text__ === 'undefined') {
            updateSection = true
            section.configuration.wb_init_calendar_title__html_text__ = `<h6 class="text-h6" style="text-align: center">${trans('wb_init_calendar_title_example')}</h6>`
          }

          if (typeof section.configuration.wb_initial_service_selection__checkbox__ === 'undefined') {
            updateSection = true
            section.configuration.wb_initial_service_selection__checkbox__ = false
          }

          if (section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_2 ||
            section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_3 ||
            section.configuration.component_id === SectionsSectionType.FLEXIBLE_BOOKING_4) {
            if (typeof section.configuration.wb_calendar_min_date__date__ === 'undefined') {
              updateSection = true
              section.configuration.wb_calendar_min_date__date__ = null
            }

            if (typeof section.configuration.wb_calendar_max_date__date__ === 'undefined') {
              updateSection = true
              section.configuration.wb_calendar_max_date__date__ = null
            }

            if (typeof section.configuration.wb_number_of_days__number__ !== 'undefined') {
              updateSection = true
              delete section.configuration.wb_number_of_days__number__
            }
          }
        }

        if (typeof section.configuration.wb_summary_text__long_text__ === 'string') {
          const currentSummary = currentBookingSummary(section.configuration.wb_summary_text__long_text__)

          if (currentSummary && currentSummary !== section.configuration.wb_summary_text__long_text__) {
            updateSection = true
            section.configuration.wb_summary_text__long_text__ = currentSummary
          }
        }

        if (updateSection) {
          DraftStateController.getInstance().updateSection(draft, section)
        }
      })
    })
  }

  public updateSection (draft: Draft, section: Section): Promise<void> {
    return api.updateWb2DraftSection(draft.id, section.id, {
      configuration: section.configuration
    })
  }

  public updatePage (draft: Draft, page: Page, changeStep: ChangeStep | null = null): Promise<void> {
    if (changeStep) {
      this.addChangeStep(changeStep)
    }
    return api.updateWb2DraftPage(draft.id, page.id, {
      type: page.type,
      name: page.name,
      route: page.route,
      action: page.action,
      auth_only: page.authOnly,
      configuration: page.configuration
    })
  }

  public createPage (draft: Draft, page: Page, changeStep: ChangeStep | null = null): Promise<Record<string, any>> {
    return new Promise((resolve, reject) => {
      api.createWb2DraftPage(draft.id, {
        type: page.type,
        name: page.name,
        route: page.route,
        action: page.action,
        auth_only: page.authOnly,
        configuration: page.configuration
      }).then((response: any) => {
        if (changeStep) {
          changeStep.newObject = plainToClass(Page, response.data)
          changeStep.oldObject = plainToClass(Page, response.data)
          this.addChangeStep(changeStep)
        }
        resolve(response)
      }).catch((error: any) => {
        reject(error)
      })
    })
  }

  public removeSection (draft: Draft, section: Section): Promise<Record<string, any>> {
    store.dispatch('builder/clearAllSteps')
    store.dispatch('sidebar/closeSidebar')
    return api.removeWb2DraftSection(draft.id, section.id)
  }

  public removePage (draft: Draft, page: Page): Promise<Record<string, any>> {
    store.dispatch('builder/clearAllSteps')
    return api.removeWb2DraftPage(draft.id, page.id)
  }

  public createSection (draft: Draft, section: Section, changeStep: ChangeStep | null = null): Promise<Record<string, any>> {
    return new Promise((resolve, reject) => {
      api.createWb2DraftSection(draft.id, {
        type: section.type,
        page_id: section.pageId,
        configuration: section.configuration,
        images: section.images
      }).then((response: any) => {
        if (changeStep) {
          changeStep.newObject = plainToClass(Section, response.data as Section)
          changeStep.oldObject = plainToClass(Section, response.data as Section)
          this.addChangeStep(changeStep)
        }
        resolve(response)
      }).catch((error: any) => {
        reject(error)
      })
    })
  }

  public updateImage (draft: Draft, section: Section, payload: SectionImagePayload): Promise<Record<string, any>> {
    return api.updateWb2DraftSectionImage(draft.id, section.id, {
      user_image_id: payload.selectedGalleryImage.defaultImage.userImageId,
      slug: payload.sectionImage.slug,
      type: payload.sectionImage.type
    })
  }

  public removeImage (draft: Draft, section: Section, image: SectionImage): Promise<Record<string, any>> {
    return api.removeWb2DraftSectionImage(draft.id, section.id, image.slug)
  }

  private getCurrentStepIndex (): number {
    return store.state.builder.currentStepIndex
  }

  private setCurrentStepIndex (index: number): void {
    store.commit('builder/SET_CURRENT_STEP_INDEX', index)
  }

  private getStepsLength (): number {
    return store.state.builder.steps.length
  }

  private hasUndoStep (): boolean {
    return this.getStepsLength() > 0 && this.getCurrentStepIndex() > 0
  }

  private hasRedoStep (): boolean {
    return this.getStepsLength() > 0 && this.getCurrentStepIndex() < this.getStepsLength()
  }

  // ADD_TO_STEPS truncates the abandoned redo branch and moves the index
  // itself, so recording a step is a single dispatch.
  public addChangeStep (changeStep: ChangeStep) {
    store.dispatch('builder/addChangeStep', changeStep)
  }

  public restoreLastStep (): Promise<RestoredStep> {
    if (!this.hasUndoStep()) {
      return Promise.reject(new Error('Can not restore - no steps'))
    }

    const previousIndex = this.getCurrentStepIndex()
    const targetIndex = previousIndex - 1

    this.setCurrentStepIndex(targetIndex)

    // A save still sitting in the debounce window carries pre-undo state; it
    // has to reach the server before the restored state is written on top.
    return this.flushPendingSaves()
      .then(() => this.restoreStep(targetIndex, RestoreType.UNDO))
      .catch((error) => {
        this.setCurrentStepIndex(previousIndex)
        throw error
      })
  }

  public restoreNextStep (): Promise<RestoredStep> {
    if (!this.hasRedoStep()) {
      return Promise.reject(new Error('Can not restore - no steps'))
    }

    const currentIndex = this.getCurrentStepIndex()

    return this.flushPendingSaves()
      .then(() => this.restoreStep(currentIndex, RestoreType.REDO))
      .then((response) => {
        this.setCurrentStepIndex(currentIndex + 1)
        return response
      })
  }

  public restoreStep (index: number, type: RestoreType): Promise<RestoredStep> {
    return new Promise((resolve, reject) => {
      const steps = store.state.builder.steps

      if (!steps.length || index < 0 || index >= steps.length) {
        reject(new Error('Can not restore - no steps'))
        return
      }

      const restoreToChangeStep: ChangeStep = steps[index]

      const draft = type === RestoreType.UNDO ? restoreToChangeStep.oldDraft : restoreToChangeStep.newDraft
      const object = type === RestoreType.UNDO ? restoreToChangeStep.oldObject : restoreToChangeStep.newObject

      if (restoreToChangeStep.type === ChangeStepType.UPDATE_DRAFT) {
        this.updateDraft(draft as Draft).then(() => {
          resolve(new RestoredStep(cloneClassObject(restoreToChangeStep), type))
        })
      } else if (restoreToChangeStep.type === ChangeStepType.UPDATE_SECTION) {
        this.updateSection(draft as Draft, object as Section).then(() => {
          resolve(new RestoredStep(cloneClassObject(restoreToChangeStep), type))
        })
      } else if (restoreToChangeStep.type === ChangeStepType.ADD_SECTION && type === RestoreType.UNDO) {
        this.removeSection(draft, object as Section).then(() => {
          store.dispatch('builder/clearFutureSteps')
          store.dispatch('sidebar/closeSidebar')

          resolve(new RestoredStep(cloneClassObject(restoreToChangeStep), type))
        })
      } else if (restoreToChangeStep.type === ChangeStepType.ADD_PAGE && type === RestoreType.UNDO) {
        this.removePage(draft, object as Page).then(() => {
          store.dispatch('builder/clearFutureSteps')
          store.dispatch('sidebar/closeSidebar')

          resolve(new RestoredStep(cloneClassObject(restoreToChangeStep), type))
        })
      } else {
        reject(new Error('Can not restore - step to restore unknown'))
      }
    })
  }

  public getCurrentPageElements (state: BuilderState, getters: any, includeEmptySection = false): DraftWithPage {
    const draft: Draft = cloneClassObject(state.draft) as Draft
    const currentPage: Page | undefined = draft.pages.find((page: Page) => page.id === getters.getCurrentPage.id)

    if (!includeEmptySection) {
      if (currentPage !== undefined) {
        if (!currentPage.sections) {
          currentPage.sections = []
        } else {
          currentPage.sections = currentPage.sections.filter((_: Section) => _.type !== SectionType.EMPTY)
        }
      }
    }

    return { draft, page: currentPage }
  }

  public checkIfPlainDraftIsMissingAnyConfiguration (draft: any) {
    if (typeof draft.configuration.wb_hide_booking_employees__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_booking_employees__checkbox__ = false
    }

    if (typeof draft.configuration.wb_og_image_url__text__ === 'undefined') {
      draft.configuration.wb_og_image_url__text__ = 'https://media.calendesk.com/demo/og-en-1.jpg'
    }

    if (typeof draft.configuration.wb_favicon_url__text__ === 'undefined') {
      draft.configuration.wb_favicon_url__text__ = null
    }

    if (typeof draft.configuration.wb_hide_signup__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_signup__checkbox__ = false
    }

    if (typeof draft.configuration.wb_hide_login__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_login__checkbox__ = !draft.configuration.wb_login_enabled__checkbox__
      delete draft.configuration.wb_login_enabled__checkbox__
    }

    if (typeof draft.configuration.wb_hide_header__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_header__checkbox__ = false
    }

    if (typeof draft.configuration.wb_hide_footer__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_footer__checkbox__ = false
    }

    if (typeof draft.configuration.wb_booking_menu_hidden__checkbox__ !== 'undefined') {
      delete draft.configuration.wb_booking_menu_hidden__checkbox__
    }

    if (typeof draft.configuration.wb_enable_discounts__checkbox__ === 'undefined') {
      draft.configuration.wb_enable_discounts__checkbox__ = false
    }

    if (draft.configuration.signup_modal_configuration &&
      typeof draft.configuration.signup_modal_configuration.wb_show_billing_data__checkbox__ === 'undefined') {
      draft.configuration.signup_modal_configuration.wb_show_billing_data__checkbox__ = false
    }

    if (typeof draft.configuration.wb_primary_language__select__ === 'undefined') {
      draft.configuration.wb_primary_language__select__ = {
        type: BuilderSelectType.LANGUAGE,
        value: store.getters['setup/getAppConfiguration'].language
      }
    }

    if (typeof draft.configuration.wb_newsletter_success_url__url__ === 'undefined') {
      draft.configuration.wb_newsletter_success_url__url__ = null
    }

    if (typeof draft.configuration.wb_hide_time_zone__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_time_zone__checkbox__ = false
    }

    if (typeof draft.configuration.wb_custom_code__long_text__ === 'undefined') {
      draft.configuration.wb_custom_code__long_text__ = null
    }

    if (typeof draft.configuration.wb_hide_booking_time_to__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_booking_time_to__checkbox__ = false
    }

    if (typeof draft.configuration.wb_hide_booking_prices__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_booking_prices__checkbox__ = false
    }

    if (typeof draft.configuration.wb_hide_payments__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_payments__checkbox__ = false
    }

    if (typeof draft.configuration.wb_hide_profile_billing_data__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_profile_billing_data__checkbox__ = false
    }

    if (typeof draft.configuration.wb_hide_profile_photo_upload__checkbox__ === 'undefined') {
      draft.configuration.wb_hide_profile_photo_upload__checkbox__ = false
    }

    if (typeof draft.configuration.wb_account_activation_success_url__url__ === 'undefined') {
      draft.configuration.wb_account_activation_success_url__url__ = null
    }

    addMissingCookieBannerKeys(draft.configuration)

    return draft
  }
}
